import { lazy, ComponentType } from 'react';

/**
 * Extended lazy component type that includes preload method
 */
export interface PreloadableComponent<T extends ComponentType<any>> 
  extends React.LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

/**
 * Wrapper around React.lazy that exposes a preload method
 * Allows preloading route components on hover or during idle time
 * 
 * @example
 * const MortgagePage = lazyWithPreload(() => import('./pages/MortgagePage'));
 * 
 * // Later, preload on hover:
 * <Link onMouseEnter={() => MortgagePage.preload()}>Bolån</Link>
 */
export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  let componentPromise: Promise<{ default: T }> | null = null;

  const load = () => {
    if (!componentPromise) {
      componentPromise = factory();
    }
    return componentPromise;
  };

  const LazyComponent = lazy(load) as PreloadableComponent<T>;
  LazyComponent.preload = load;

  return LazyComponent;
}
